<script>
import { validationMixin } from 'vuelidate'
import { required, email, minLength, helpers } from 'vuelidate/lib/validators'
import Confirmation from '@/components/widgets/modals/Confirmation'
import { getToolsClient } from '@/services/tools'
import { Helper } from '@/mixin/helper'
import $store from '@/state/store'

const alpha = helpers.regex('alpha', /^[a-zA-Z0-9-_.]*$/)
const toolsAxios = getToolsClient()

export default {
  components: { Confirmation },

  mixins: [validationMixin, Helper],

  props: {
    modal: {
      type: Boolean,
      default: false,
    },

    isEdit: {
      type: Boolean,
      default: false,
    },

    detail: {
      type: Object,
      default: () => {},
    },

    region: {
      type: String,
      default: 'SG'
    }
  },

  data() {
    return {
      form: {
        companyName: null,
        domainName: null,
        userEmail: null,
        isSendEmail: true,
        password: null,
        resourceUrl: null,
        offlineTimeout: null,
        asChild: null,
        asWhitelabel: null,
        parentDomainId: null,
      },
      modalConfirm: false,
      loading: false,
      showPassword: false,
      companyId: null,
      isProduction: false,
      isAdminUserHasLogin: false,
      isInputChanged: false,
      isAdditionalInformation: false,
      parentCompany: {},
      isLoading: false,
    }
  },
  mounted() {
    if (process.env.NODE_ENV == 'production') {
      this.isProduction = false
    }
  },
  validations() {
    const form = {
      companyName: { required },
      domainName: { required, alpha },
    }
    form.userEmail = { required, email }
    if (!this.isEdit) {
      if (this.isProduction == true) {
        form.password = { required, minLength: minLength(6) }
      }
    } else {
      this.form.resourceUrl = this.detail.resourceUrl
    }
    return { form }
  },

  computed: {
    showModal: {
      get() {
        return this.modal
      },
      set(newVal) {
        this.$emit('update:modal', newVal)
      },
    },

    modalTitle() {
      if (this.isEdit) {
        return this.$t('domain.editDomain')
      }
      return this.$t('domain.addDomain')
    },

    edit: {
      set(newVal) {
        this.$emit('update:isEdit', newVal)
      },
      get() {
        return this.isEdit
      },
    },
  },
  watch: {
    form: {
      handler(newVal) {
        this.isInputChanged = JSON.stringify(newVal) !== JSON.stringify(this.originalForm);
      },
      deep: true,
    },
  },
  async created() {
    if (this.detail && this.isEdit) {
      if (this.detail.hasLogin == true) {
        this.isAdminUserHasLogin = true
      } else {
        this.isAdminUserHasLogin = false
      }

      this.form.companyName = this.detail.companyName
      this.form.offlineTimeout = this.detail.offlineTimeout
      this.form.isSendEmail = this.isEdit ? false : true
      this.form.asChild = this.detail.resellerId !== null && !this.detail.isParent
      this.form.asWhitelabel = this.detail.isWhitelabel === true
      this.form.parentDomainId = this.detail.resellerId
      if (this.detail.resellerId !== null && !this.detail.isParent) {
        this.parentCompany = await this.findParent(this.detail.resellerId)
      }

      let searchString
      if (process.env.VUE_APP_HOSTED_ZONE_NAME) {
        searchString = '.' + process.env.VUE_APP_HOSTED_ZONE_NAME
      }

      const lastIndex = this.detail.domainName.lastIndexOf(searchString)
      if (lastIndex !== -1) {
        const modifiedString =
          this.detail.domainName.slice(0, lastIndex) +
          this.detail.domainName.slice(lastIndex + searchString.length)

        this.form.domainName = modifiedString
      } else {
        this.form.domainName = this.detail.domainName
      }
      this.form.userEmail = this.detail.email
      this.companyId = this.detail.companyId
      this.form.resourceUrl = this.detail.resourceUrl
      // Save a copy of the original data when the component is created
      this.originalForm = { ...this.form };
    } else {
      this.isAdminUserHasLogin = false
    }
  },

  methods: {
    handleSubmit(bvModalEvent) {
      bvModalEvent.preventDefault()
      this.$v.form.$touch()
      if (this.$v.form.$error) {
        return
      }
      if (!this.isInputChanged) {
        this.closeModal()
        return
      }
      this.modalConfirm = true
    },

    async resendEmail() {
      const url = 'masterdata/send-invitation-user'
      const payload = {
        resource_url: this.form.resourceUrl,
      }
      const options = {
        method: 'POST',
        url: url,
        data: payload,
      }

      await toolsAxios(options)
        .then(() => {
          this.$toast.success('Successfully Send Email')
        })
        .catch(() => {
          this.$toast.error('Failed Sending Email')
        })
    },

    async onSubmit() {
      this.loading = true
      const url = 'company'
      if (this.form.userEmail) {
        this.form.userEmail = this.form.userEmail.toLowerCase()
      }
      const payload = {
        companyName: this.form.companyName,
        domainName: this.form.domainName,
        isSendEmail: this.form.isSendEmail,
        userEmail: this.form.userEmail,
        isWhitelabel: !this.form.asWhitelabel ? false : true,
        parentId: this.form.asChild === true ? this.form.parentDomainId : null,
      }

      if (this.isAdditionalInformation) {
        payload.offlineTimeout = parseInt(this.form.offlineTimeout)
      }

      if (!this.isEdit) {
        payload.userEmail = this.form.userEmail

        if (this.isProduction == true) {
          payload.password = this.form.password
        }
      }

      const options = {
        method: this.isEdit ? 'PATCH' : 'POST',
        url: this.isEdit ? `${url}/${this.companyId}` : url,
        data: payload,
        headers: {
          'X-Region': this.region,
        }
      }

      await toolsAxios(options)
        .then(() => {
          this.$toast.success(
            this.isEdit ? this.$t('editDomainSuccess') : this.$t('addDomainSuccess')
          )
          if (this.form.isSendEmail && this.isEdit) {
            this.resendEmail()
          }
          this.$emit('refetch')
        })
        .catch((error) => {
          this.handleError(error)
        })
      this.loading = false
      this.closeModal()
      this.resetForm()
    },

    async getCompanies(param) {
      try {
        if (!param) return
        this.isLoading = true
        const filters = param.filters ? `${param.filters};isActive:eq:true` : 'isActive:eq:true'
        const companies = await toolsAxios.get('/company', {
          params: { ...param, filters: filters },
        })
        this.isLoading = false
        const filteredData = companies?.data.data.filter(
          (val) => val.resellerId === null && val.companyId !== this.detail?.companyId
        )
        companies.data.data = filteredData
        return companies.data
      } catch (error) {
        console.log(error)
      }
    },

    async findParent(parentId) {
      try {
        const res = await this.getCompanies({
          filters: `companyId:eq:${parentId}`,
        })
        return res.data[0]
      } catch (error) {
        console.log(error)
      }
    },

    handleError(error) {
      if (error.response) {
        const errorStatus = error.response.status
        const errorData = error.response.data

        if (errorStatus === 400 && errorData.details) {
          errorData.details.forEach((element) => {
            this.$toast.error(`${errorData.error}: ${element.field} ${element.message}`)
          })
        } else if (errorStatus === 409 && errorData.details) {
          errorData.details.forEach((element) => {
            this.$toast.error(`${errorData.error}: ${element.message}`)
          })
        } else if (errorStatus === 401) {
          this.$toast.error(errorData.error)
          localStorage.removeItem('token')
          localStorage.removeItem('tokenAccess')
          this.$router.push({ name: 'Login' })
        } else if (errorStatus === 403) {
          this.$toast.error(errorData.error)
        } else {
          this.$toast.error(errorData.error)
        }
      } else if (error.request) {
        this.$toast.error(error.response.statusText)
      } else {
        this.$toast.error(error.message)
      }
    },

    togglePassword() {
      this.showPassword = !this.showPassword
    },

    closeModal() {
      this.showModal = false
      this.edit = false
    },

    resetForm() {
      this.form.companyName = null
      this.form.domainName = null
      this.form.userEmail = null
      this.form.offlineTimeout = null
    },
    handleOpen(open) {
      if (open) {
        document.querySelector('div#modal-form-domain___BV_modal_content_').style.height = '900px'
      } else {
        document.querySelector('div#modal-form-domain___BV_modal_content_').style.height = '700px'
      }
    },
    hideAddDomainButton() {
      return $store.state.config.hideAddDomainButton
    },
  },
}
</script>

<template>
  <div>
    <b-modal
      v-model="showModal"
      class="mc-modal"
      id="modal-form-domain"
      :title="modalTitle"
      size="md"
      centered
      :hide-header-close="loading"
      no-close-on-backdrop
      @close="closeModal"
    >
      <div class="card-body mc-modal__filter p-2">
        <b-form @submit.stop.prevent="handleSubmit">
          <b-row>
            <b-col cols="12">
              <label class="required">{{ $t('companyName') }}</label>
              <b-form-input
                v-model="form.companyName"
                :placeholder="$t('companyName')"
                :class="{
                  'is-invalid': $v.form.companyName.$error,
                }"
              >
              </b-form-input>
              <div v-if="$v.form.companyName.$error" class="invalid-feedback">
                <span v-if="!$v.form.companyName.required">{{ $t('sensor.required') }}</span>
              </div>
            </b-col>
            <b-col cols="12">
              <label class="mt-3 required">{{ $t('domain.table.domainName') }}</label>
              <b-form-input
                v-model="form.domainName"
                :placeholder="$t('domain.table.domainName')"
                :class="{
                  'is-invalid': $v.form.domainName.$error,
                }"
                :disabled="this.hideAddDomainButton()"
              >
              </b-form-input>
              <div v-if="$v.form.domainName.$error" class="invalid-feedback">
                <span v-if="!$v.form.domainName.required">{{ $t('sensor.required') }}</span>
                <span v-else-if="!$v.form.domainName.point">{{ $t('error.charNotAllowed') }}</span>
              </div>
            </b-col>
            <b-col cols="12">
              <label class="mt-3 required">{{ $t('email') }}</label>
              <b-form-input
                v-model="form.userEmail"
                placeholder="ex: johndoe@mail.com"
                :class="{
                  'is-invalid': $v.form.userEmail.$error,
                }"
                :disabled="isAdminUserHasLogin || this.hideAddDomainButton()"
              >
              </b-form-input>
              <div v-if="$v.form.userEmail.$error" class="invalid-feedback">
                <span v-if="!$v.form.userEmail.required">{{ $t('sensor.required') }}</span>
                <span v-else-if="!$v.form.userEmail.email">{{ $t('invalidEmail') }}</span>
              </div>
            </b-col>
            <!-- <b-col v-if="isEdit&&!isProduction" cols="12">
              <b-button class="mt-3" @click="resendEmail" variant="primary" :disabled="isAdminUserHasLogin">
                Kirim Ulang Email Atur Kata Sandi
              </b-button>
            </b-col> -->
            <b-col v-if="!isEdit && isProduction" cols="12">
              <b-form-group class="mb-2" id="input-group-2" label-for="input-2">
                <label class="mt-3 required">Password</label>
                <div
                  class="form-password"
                  :class="{
                    'is-invalid': $v.form.password.$error,
                  }"
                >
                  <b-form-input
                    id="input-2"
                    v-model="form.password"
                    :type="showPassword ? 'text' : 'password'"
                    placeholder="Password"
                    :class="{
                      'is-invalid': $v.form.password.$error,
                    }"
                  >
                  </b-form-input>
                  <i
                    @click="togglePassword"
                    class="mc-fa-password"
                    :class="{
                      'icon-outline_eye-off': showPassword,
                      'icon-outline_eye': !showPassword,
                    }"
                  />
                </div>
                <div v-if="$v.form.password.$error" class="invalid-feedback">
                  <span v-if="!$v.form.password.required"> This field is required </span>
                  <span v-else-if="!$v.form.password.minLength">
                    Password must have at least
                    {{ $v.form.password.$params.minLength.min }} characters</span
                  >
                </div>
              </b-form-group>
            </b-col>
            <b-col v-if="!isProduction" cols="12" class="mt-3">
              <b-form-group class="mb-2" id="input-group-2" label-for="input-2">
                <div>
                  <input
                    class="form-check-input me-1"
                    type="checkbox"
                    v-model="form.isSendEmail"
                    aria-label="..."
                    :disabled="isEdit && isAdminUserHasLogin"
                  />
                  {{ $t('sendEmail') }}
                </div>
              </b-form-group>
            </b-col>
            <b-col sm="12">
              <div class="mt-3">
                <div class="form-check-right form-switch">
                  <input
                    class="form-check-input ms-2"
                    type="checkbox"
                    id="SwitchCheckSizemd"
                    v-model="isAdditionalInformation"
                    v-b-toggle.accordion-3
                  />
                  <label class="" for="SwitchCheckSizemd"> Informasi Lainnya </label>
                </div>
              </div>
            </b-col>
            <div v-if="isAdditionalInformation" style="margin-top: 10px">
              <b-col cols="12">
                <label>{{ $t('offlineTimeout') }}</label>
                <div class="d-flex align-items-center pe-1">
                  <b-form-input
                    type="number"
                    v-model="form.offlineTimeout"
                    :placeholder="$t('offlineTimeout')"
                    style="width: 90%; margin-right: 10px"
                    @keydown="
                      portKeydown($event, (preventZeroFirst = false), (preventComma = true))
                    "
                    :disabled="!isEdit"
                  >
                  </b-form-input>
                  Menit
                </div>
              </b-col>
              <b-col>
                <div class="mt-3 row">
                  <div class="mc-parent-domain col-6">
                    <input
                      v-model="form.asChild"
                      :class="`form-check-input me-1 ${
                        detail?.isParent && isEdit ? 'mc-disabled' : ''
                      }`"
                      type="checkbox"
                      :disabled="detail?.isParent && isEdit"
                    />
                    <label :class="`${detail?.isParent && isEdit ? 'text-muted' : ''}`">{{
                      $t('domain.asChild')
                    }}</label>
                  </div>
                  <div class="mc-child-domain col-6">
                    <input
                      class="form-check-input me-1"
                      type="checkbox"
                      v-model="form.asWhitelabel"
                    />
                    <label>{{ $t('domain.asWhitelabel') }}</label>
                  </div>
                </div>
              </b-col>
              <b-col cols="12" v-if="form.asChild" class="mt-2">
                <FormulateInput
                  ref="formulateInputDomainRef"
                  type="select-resourceful"
                  v-model="form.parentDomainId"
                  label="Select Parent Domain"
                  placeholder="Pilih Parent"
                  class="p-0"
                  :optionsGetter="getCompanies"
                  :optionLabelFn="
                    ({ item }) => {
                      return `${item.companyName} (${item.domainName})`
                    }
                  "
                  :initialObject="parentCompany ?? {}"
                  trackBy="companyId"
                  @openDropdown="handleOpen"
                  :overrideFilters="{
                    page: 'page',
                    size: 'limit',
                    q: 'search',
                  }"
                />
              </b-col>
            </div>
            <!-- </b-collapse> -->
          </b-row>
        </b-form>
      </div>
      <template #modal-footer>
        <b-row class="mc-modal__filter-footer mx-0">
          <b-col cols="12" class="mc-modal__filter-footer-btn">
            <b-button
              variant="outline-primary"
              :disabled="loading"
              @click="closeModal"
              class="me-3"
            >
              {{ $t('global.cancel') }}
            </b-button>
            <b-button @click="handleSubmit" variant="primary" :disabled="loading">
              <b-spinner v-if="loading" small type="grow"></b-spinner>
              {{ loading ? $t('loading') + '...' : $t('global.save') }}
            </b-button>
          </b-col>
        </b-row>
      </template>
    </b-modal>
    <confirmation v-if="modalConfirm" :modal.sync="modalConfirm" @confirmed="onSubmit" />
  </div>
</template>
